import _ from 'lodash';
import LogRocket from 'logrocket';

import MainMarker from 'Images/generic_marker.png';
import BlueMarker from 'Images/generic_blue_marker.png';


export default {
  name: 'siteselect',
  template: `
    <div class="site-select-container">
      <div class="container-header flex gap-4 align-items-center">
        <h1 class="page-title">Sites</h1>
        <p class="page-description">Select a site to view its details and equipment</p>
      </div>
      <div class="site-select-content">
        <div id="map_canvas" class="map"></div>
      </div>
    </div>
  `,
  controllerAs: 'SiteSelCtrl',
  controller: SiteSelectController
};

const STREAMABLE_EQUIPMENT_TYPES = new Set(['cameras', 'radar']);

/* @ngInject */
function SiteSelectController(
  $element,
  $scope,
  $compile,
  $location,
  $window,
  $timeout,
  $filter,
  SiteService,
  EquipmentInfoService,
  EventInfoService,
  MapService,
) {
  const vm = this;
  const optLat = 0;
  const optLng = 0;
  const ICON_WIDTH = 25;
  const ANCHOR_LAT = ICON_WIDTH / 2;
  const ICON_HEIGHT = 33;
  const bounceLength = 2150;
  const infoWindowTTL = 10000;
  const markers = {};
  const equipmentObj = {};
  const polygons = [];
  const _sites_ = {};
  const getSite = id => _sites_[id];
  const addSite = (site) => {
    _sites_[site.id] = site;
  };

  let map;
  let cluster;
  let oms;
  let equipments = [];
  let datInfoBox;
  let infoZIndex = 1;

  const ignoredEvents = _.get(EventInfoService, 'eventsConfig.eventPanelExclusions', []);
  const rcuCameraList = {};

  vm.$onInit = init;
  vm.$postLink = postLink;
  vm.$onDestroy = onDestroy;
  vm.close = close;
  vm.viewDashboard = viewDashboard;
  vm.showInfoBox = showInfoBox;
  vm.stopListeners = stopListeners;

  async function init() {
    _.forEach(await SiteService.get(), site => addSite(angular.copy(site)));
    EquipmentInfoService.setNeed('dashboard');
    equipments = await EquipmentInfoService.getAllEquipments(eq => MapService.eqIsMappable(eq));
    initMapWatchers();
    $scope.$apply();
  }

  function initMapWatchers() {
    map = new MapService.Map($element.find('#map_canvas')[0], 'siteselect', {
      center: new google.maps.LatLng(optLat, optLng),
      zoom: 3,
      minZoom: 3,
      backgroundColor: '#34383F',
      gestureHandling: 'auto'
    });
    cluster = new MapService.MarkerCluster(map, {
      gridSize: 25,
      maxZoom: 13
    });
    oms = new MapService.MarkerSpiderfier(map);
    oms.addListener('click', (marker) => {
      showInfoBox(marker.id);
    });
    _.forEach(equipments, (eq) => {
      const { id, equipmentType, rcu, settings: { latitude, longitude }, site: siteId } = eq;
      equipmentObj[id] = _.merge(equipmentObj[id], eq);
      eq = equipmentObj[id];
      if (equipmentType === 'cameras') {
        rcuCameraList[rcu] = id;
      }
      if (!latitude || !longitude) {
        return;
      }
      const image = {
        url: MainMarker,
        size: { lat: ICON_WIDTH, lng: ICON_HEIGHT },
        origin: { lat: 0, lng: 0 },
        anchor: { lat: ANCHOR_LAT, lng: ICON_HEIGHT }
      };
      let marker = {};
      if (siteId) {
        if (!markers[siteId]) {
          createSiteMarker(siteId);
        }
        // For all equipment belonging to sites, add an equipment marker
        const equipmentMarker = new MapService.Marker(map,
          eq,
          image,
          false,
          { visible: false }
        );
        oms.addMarker(equipmentMarker);
        markers[id] = equipmentMarker;
      } else {
        marker = new MapService.Marker(null, eq, image);
        cluster.addMarker(marker);
        oms.addMarker(marker);
        markers[id] = marker;
      }

      const debouncedClose = _.debounce(() => {
        if (eq.infowindow && eq.timeout) {
          eq.infowindow.close();
          eq.timeout = null;
        }
      }, infoWindowTTL);

      // new event open stream
      $scope.$on(`event:${id}`, (__, eventObj) => {
        if (!_.includes(ignoredEvents, eventObj.eventName)) {
          let dashboard = '';
          if (eq.dashboards && eq.dashboards.length > 0) {
            dashboard = eq.dashboards[0].id;
          }
          const contentString =
                  `<div ng-click="SiteSelCtrl.stopListeners('${eventObj.equipment.id}')">
                          <h4 class="text-center">${eq.name}</h4>
                          <div class="text-center">
                            <p>${eventObj.description}</p>
                            <div>${$filter('date')(eventObj.eventDate, 'medium')}</div>
                            <span class="btn btn-sm" ng-click="SiteSelCtrl.viewDashboard('${dashboard}')">
                              <i class="far fa-tachometer view-icon"></i>
                              Dashboard
                            </span>
                            <span class="btn btn-sm" ng-click="SiteSelCtrl.showInfoBox('${eventObj.equipment.id}')">
                              <i class="far fa-video view-icon"></i>
                              View
                            </span>
                          </div>
                        </div>`;
          const compiled = $compile(contentString)($scope);
          if (_.has(eq, 'infowindow')) {
            eq.infowindow.setContent(compiled[0]);
            eq.infowindow.setZIndex(infoZIndex);
            if (!eq.infowindow.view) {
              eq.infowindow.open(map, new google.maps.MVCObject());
            }
          } else {
            const offset = { width: 0, height: -35 };
            const infowindow = new google.maps.InfoWindow({
              content: compiled[0],
              maxWidth: 400,
              disableAutoPan: true,
              enableEventPropagation: true,
              pixelOffset: new google.maps.Size(offset.width, offset.height),
              zIndex: infoZIndex
            });
            eq.infowindow = infowindow;

            const info = new google.maps.MVCObject();

            eq.infowindow.open(map, info);
            eq.infowindow.setPosition(marker.getPosition());
          }
          marker.setAnimation(google.maps.Animation.BOUNCE);

          infoZIndex += 1;

          $timeout(() => {
            marker.setAnimation(null);
          }, bounceLength);

          eq.timeout = true;
          debouncedClose();
        }
      });
    });

    MapService.appendPinNotificationsControls(map, _.map(equipments, 'id'));

    if (!_.isEmpty(equipments)) {
      MapService.fitZoomMap(map, Object.values(markers));
    }
  }

  function postLink() {
    // close must be performed before destroy, otherwise datInfoBox may be undefined before it can be used
    const toggleRouteChangeListener = $scope.$on('$stateChangeStart', () => {
      close();
      // this deletes the listener once it changes.
      toggleRouteChangeListener();
      closeAllInfoWindows();
    });
  }

  function onDestroy() {
    EquipmentInfoService.unsubscribeToGivenEquipment(_.map(equipments, 'id'));
    cluster.setMap(null);
    oms.clearMarkers();
    oms.clearListeners('click');
    MapService.unsetMarkers(Object.values(markers));
    polygons.forEach(polygon => polygon.setMap(null));
    MapService.destroyGoogleMaps('#map_canvas');
  }

  function close() {
    // This will close all info windows when user tries to open another or clicks the close button.
    if (datInfoBox) {
      const content = datInfoBox.getContent();
      const scope = datInfoBox._scope;
      if (scope) {
        scope.$destroy();
      }
      $(content).remove();
      MapService.removeIntervalsAndCloseInfoWins(datInfoBox);
    }

  }

  function closeAllInfoWindows() {
    _.forEach(equipmentObj, (eq) => {
      if (eq.infowindow) {
        eq.infowindow.close();
      }
    });
  }

  function viewDashboard(id) {
    $location.path(`/dashboard/${id}`);
  }


  function stopListeners(idEquipment) {
    if (equipmentObj[idEquipment].timeout) {
      equipmentObj[idEquipment].timeout = null;

    }
    equipmentObj[idEquipment].infowindow.setZIndex(infoZIndex);
    infoZIndex += 1;
  }

  function showInfoBox(idEquipment) {
    close();

    if (_.has(equipmentObj, `${idEquipment}.infowindow`)) {
      equipmentObj[idEquipment].infowindow.close();
      if (equipmentObj[idEquipment].timeout) {
        equipmentObj[idEquipment].timeout = null;
      }
    }
    EquipmentInfoService.getEquipmentById(idEquipment)
      .then((equipment) => {
        let dashboard = '';
        if (equipment.dashboards && equipment.dashboards.length > 0) {
          dashboard = equipment.dashboards[0].id;
        }

        const contentEquip = new ContentEquip(equipment);
        if (datInfoBox) {
          datInfoBox.setContent(new MapService.CameraContentBox(contentEquip, 'SiteSelCtrl', dashboard));
        } else {
          datInfoBox = createInfoBox(contentEquip, dashboard);
        }
        const newScope = $scope.$new(false, $scope);
        $compile(datInfoBox.content_)(newScope);
        datInfoBox._scope = newScope;
        const marker = markers[idEquipment];

        vm.activeCameraId = datInfoBox.cameraId;
        datInfoBox.open(map, marker);
        // workaround until google fixes their shit: https://issuetracker.google.com/issues/111997760
        setTimeout(() => {
          datInfoBox.content_.parentNode.parentNode.parentNode.style.width = '100%';
        }, 100);
      });
  }

  function createInfoBox(eq, dash) {
    // create different popups based on equipment type.
    const box = new MapService.CameraContentBox(eq, 'SiteSelCtrl', dash);

    const minCompare = 337;
    const multiByWidth = 0.7;
    const height = Math.min(parseInt($window.innerWidth * multiByWidth, 10), minCompare);
    const divByHeight = 2;
    const negPix = -35;
    const pixsOff = -400;

    return new MapService.Infobox(
      eq.id,
      {
        content: box,
        boxStyle: {
          height: `${height}px`
        },
        pixelOffset: new google.maps.Size(pixsOff / divByHeight, negPix),
        zIndex: 100_000, // has to be high to give room to the infowindow's zIndex
        enableEventPropagation: false
      }
    );
  }

  function createSiteMarker(siteId) {
    const site = getSite(siteId);
    // Safety net for *misconfigured* equipment associated to sites the user cannot access
    if (!site || !site.border) {
      return void LogRocket.captureException(
        new Error(site ? 'Site with no border' : 'Misconfigured equipment on siteSelect'),
        {
          extra: {
            equipment: _.find(equipments, { site: siteId })?.id,
            site: siteId,
          }
        }
      );
    }
    const blueImage = {
      url: BlueMarker,
      size: { lat: ICON_WIDTH, lng: ICON_HEIGHT },
      origin: { lat: 0, lng: 0 },
      anchor: { lat: ANCHOR_LAT, lng: ICON_HEIGHT }
    };
    // MapService is expecting lat & long to live in settings like equipment
    site.settings = {
      latitude: site.latitude,
      longitude: site.longitude
    };
    const siteMarker = new MapService.Marker(map, site, blueImage, false, { zIndex: 1 });
    const polygon = new MapService.Polygon(map, site.border, false);
    const zoomAroundPolygon = MapService.getBoundsZoomLevel(polygon.getBounds());
    const bounds = new google.maps.LatLngBounds();

    // Add a listener to the site marker that zooms and toggles marker visibility
    siteMarker.addListener('click', () => {
      siteMarker.setVisible(false);
      _.forEach(site.equipment, (id) => {
        const marker = markers[id];
        if (marker) {
          marker.setVisible(true);
          bounds.extend(marker.position);
        }
      });
      zoomAroundPolygon <= MapService.getBoundsZoomLevel(bounds) ? // zoom to what is further out
        map.fitBounds(polygon.getBounds()) :
        map.fitBounds(bounds);
    });
    google.maps.event.addListener(map, 'zoom_changed', () => {
      const zoom = map.getZoom(); // Is there better way than a zoom listener for each site?
      const isVisible = zoom <= (zoomAroundPolygon - 3); // sweet spot to flip between marker and polygon
      siteMarker.setVisible(isVisible);
      polygon.setVisible(!isVisible);
      _.forEach(site.equipment, (id) => {
        const marker = markers[id];
        if (marker) {
          marker.setVisible(!isVisible);
          bounds.extend(marker.position);
        }
      });
    });
    polygons.push(polygon); // save this polygon so we can remove all from map later
    markers[siteId] = siteMarker;
  }

  function ContentEquip(equipment) {
    const { name, id, equipmentType, rcu } = equipment;
    const camId = STREAMABLE_EQUIPMENT_TYPES.has(equipmentType) ? id : rcuCameraList[rcu];
    return {
      name,
      id: camId
    };
  }
}
